import { IDB_filesMeta, IDB_notes, IDB_syncParamsNotes, IDB_update } from '../IDB/IDB.dexie'
import { Serv_client } from '../Serv/Serv.client'
import { omit } from '../../../shared/lib/functional/object.no-deps'
import { Note_toNote } from './Note.Note'
import { map } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { type Sync_Params, Sync_standardSync } from '../Sync/Sync.standardSync'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import type { FileId, FileMetaApp } from '../../../shared/domain/files/types.no-deps'
import type { Note, AppNote } from '../../../shared/domain/notes/types.no-deps'
import { File_getMetasToFetch } from '../File/File.Meta'
import { Imag_cacheImages } from '../Imag/Imag.cache'
import { findNodes } from '../../../shared/domain/notes/lexicalNode.no-deps'
import { fakeFetchUrl } from '../../../app-core/lib/file/withS3Url'

export async function Note_sync(logger: ClientLogger) {
    logger.log(`Note_sync`)

    await Sync_standardSync({
        params: IDB_syncParamsNotes(),
        sync,
        write,
        clear: () => IDB_notes().clear(),
    })
}

export async function Note_saveNote(note: AppNote) {
    const currentTime = new Date()

    await IDB_notes().add({
        id: note.id,
        title: note.title,
        content: note.content,
        createdAt: note.createdAt || currentTime,
        lastModified: currentTime,
        status: 'pendingUpload',
    })
}

type Synced = {
    notes: Note[]
    fileMetas: FileMetaApp[]
}

async function sync(params: Sync_Params) {
    const notes = await IDB_notes().where({ status: 'pendingUpload' }).toArray()
    const toSync = notes.map((entry) => omit(entry, 'status'))

    const response = await Serv_client.syncNotes(toSync, params.lastSyncTime)

    const fileIds: FileId[] = response.notes
        .flatMap((note) => findNodes(note.content.root, (node) => node.type === 'file'))
        .map((node) => node.fileId)
    const fileIdsToFetch = await File_getMetasToFetch(fileIds)
    const fileMetas = await Serv_client.getFileMetaBulk({ fileIds: fileIdsToFetch }).then((r) => r.fileMeta)

    const imageUrls = response.notes
        .flatMap((note) => findNodes(note.content.root, (node) => node.type === 'photo'))
        .map((node) => node.fileId)
        .map((fileId) => fakeFetchUrl(fileId))

    const videoThumbUrls = response.notes
        .flatMap((note) => findNodes(note.content.root, (node) => node.type === 'video'))
        .map((node) => node.fileId)
        .map((fileId) => fakeFetchUrl(fileId, true))

    await Imag_cacheImages([...imageUrls, ...videoThumbUrls])

    await pipe(
        notes,
        map((entry) => IDB_update(IDB_notes(), entry.id, { status: 'synced' })),
        (promises) => Promise.all(promises)
    )

    return { ...response, fileMetas, teams: params.teams }
}

async function write<T extends Synced>(response: T) {
    const notes = response.notes.map((entry) => Note_toNote(entry, 'synced'))

    // Store the received notes in the `notes` IDB table.
    await IDB_notes().bulkPut(notes)
    await IDB_filesMeta().bulkPut(response.fileMetas)
}
